import React, { useState, useEffect, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import './AdminErrors.css';

function AdminErrors() {
  const { user } = useContext(UserContext);

  // State
  const [errors, setErrors] = useState([]);
  const [failedAudio, setFailedAudio] = useState([]);
  const [chunks, setChunks] = useState({ by_session: [], unknown_by_date: [], total_count: 0 });
  const [stats, setStats] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('errors');
  const [expandedError, setExpandedError] = useState(null);
  const [retryLoading, setRetryLoading] = useState(null);

  // Chunk-specific state
  const [sessionIdSearch, setSessionIdSearch] = useState('');
  const [sessionChunks, setSessionChunks] = useState(null);
  const [chunkLoading, setChunkLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    clinic_id: '',
    user_id: '',
    error_code: '',
    from_date: '',
    to_date: '',
    has_transcription: ''
  });

  // Fetch data
  useEffect(() => {
    if (user?.is_admin) {
      fetchAll();
    }
  }, [user]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchErrors();
    }
  }, [filters, user]);

  // Admin check - must be after hooks
  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchErrors(),
        fetchFailedAudio(),
        fetchChunks(),
        fetchStats(),
        fetchClinicsAndUsers()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchErrors = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const response = await apiClient.get('/api/admin/errors', { params });
      setErrors(response.data.errors || []);
    } catch (error) {
      console.error('Error fetching errors:', error);
    }
  };

  const fetchFailedAudio = async () => {
    try {
      const response = await apiClient.get('/api/admin/failed-audio');
      setFailedAudio(response.data.files || []);
    } catch (error) {
      console.error('Error fetching failed audio:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/api/admin/error-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchClinicsAndUsers = async () => {
    try {
      const response = await apiClient.get('/api/admin/clinics-users');
      setClinics(response.data.clinics || []);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching clinics and users:', error);
    }
  };

  const fetchChunks = async () => {
    try {
      const response = await apiClient.get('/api/admin/chunks');
      setChunks(response.data || { by_session: [], unknown_by_date: [], total_count: 0 });
    } catch (error) {
      console.error('Error fetching chunks:', error);
    }
  };

  const searchSessionChunks = async (overrideSessionId = null) => {
    const sessionId = overrideSessionId || sessionIdSearch.trim();
    if (!sessionId) {
      alert('Ange ett sessions-ID');
      return;
    }

    if (overrideSessionId) {
      setSessionIdSearch(String(overrideSessionId));
    }

    setChunkLoading(true);
    try {
      const response = await apiClient.get(`/api/admin/session-chunks/${sessionId}`);
      setSessionChunks(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        alert('Session hittades ej');
      } else {
        alert(`Fel: ${error.response?.data?.error || error.message}`);
      }
      setSessionChunks(null);
    } finally {
      setChunkLoading(false);
    }
  };

  const handleReprocessSession = async (sessionId) => {
    if (!window.confirm(`Vill du transkribera om alla chunks för session ${sessionId}?\n\nDetta kommer att:\n1. Rensa befintlig transkription\n2. Transkribera alla chunk-filer\n3. Ta bort filerna efter lyckad transkribering`)) {
      return;
    }

    setReprocessing(true);
    try {
      const response = await apiClient.post('/api/admin/reprocess-session', {
        session_id: sessionId,
        clear_transcription: true
      });

      if (response.data.success) {
        const { chunks_processed, chunks_failed, transcription_length } = response.data;
        alert(`Klart!\n\nTranskriberade: ${chunks_processed} filer\nMisslyckades: ${chunks_failed}\nTranskription: ${transcription_length} tecken\n\nÖppna sessionen i appen för att generera journal.`);
        fetchChunks();
        if (sessionChunks) {
          searchSessionChunks();
        }
      }
    } catch (error) {
      alert(`Fel: ${error.response?.data?.error || error.message}`);
    } finally {
      setReprocessing(false);
    }
  };

  const handleDeleteChunks = async (sessionId) => {
    if (!window.confirm(`Vill du ta bort alla chunk-filer för session ${sessionId}?\n\nDetta kan inte ångras!`)) {
      return;
    }

    try {
      const response = await apiClient.post('/api/admin/delete-chunks', {
        session_id: sessionId
      });

      if (response.data.success) {
        alert(`Borttaget: ${response.data.deleted_count} filer`);
        fetchChunks();
        setSessionChunks(null);
      }
    } catch (error) {
      alert(`Fel: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      clinic_id: '',
      user_id: '',
      error_code: '',
      from_date: '',
      to_date: '',
      has_transcription: ''
    });
  };

  const handleRetryJournal = async (sessionId) => {
    if (!sessionId) {
      alert('Ingen session kopplad till detta fel');
      return;
    }

    setRetryLoading(`journal-${sessionId}`);
    try {
      const response = await apiClient.post('/api/admin/retry-journal', {
        session_id: sessionId
      });

      if (response.data.success) {
        alert(`Journal genererad! Längd: ${response.data.journal_length} tecken`);
        fetchErrors();
        fetchStats();
      }
    } catch (error) {
      alert(`Fel: ${error.response?.data?.error || error.message}`);
    } finally {
      setRetryLoading(null);
    }
  };

  const handleRetryTranscription = async (filename, generateJournal = false) => {
    setRetryLoading(`transcription-${filename}`);
    try {
      const response = await apiClient.post('/api/admin/retry-transcription', {
        filename,
        generate_journal: generateJournal
      });

      if (response.data.success) {
        let message = `Transkribering lyckades! ${response.data.transcript_length} tecken`;
        if (response.data.journal_generated) {
          message += `\nJournal genererad: ${response.data.journal_length} tecken`;
        }
        alert(message);
        fetchFailedAudio();
        fetchStats();
      }
    } catch (error) {
      alert(`Fel: ${error.response?.data?.error || error.message}`);
    } finally {
      setRetryLoading(null);
    }
  };

  const handleResolveError = async (errorId) => {
    const note = prompt('Anteckning om lösning (valfritt):');
    if (note === null) return; // Cancelled

    try {
      await apiClient.put(`/api/admin/errors/${errorId}/resolve`, {
        resolution_note: note
      });
      fetchErrors();
      fetchStats();
    } catch (error) {
      alert(`Fel: ${error.response?.data?.error || error.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('sv-SE');
    } catch {
      return dateString;
    }
  };

  const getErrorCodeClass = (code) => {
    if (code?.startsWith('JE')) return 'error-code-journal';
    if (code?.startsWith('TE')) return 'error-code-transcription';
    return '';
  };

  // Filter users by selected clinic
  const filteredUsers = filters.clinic_id
    ? users.filter(u => u.clinic_id === parseInt(filters.clinic_id))
    : users;

  // Get unique error codes from current errors
  const errorCodes = [...new Set(errors.map(e => e.error_code))].filter(Boolean);

  return (
    <div className="admin-errors-container">
      <h2>
        <i className="fas fa-exclamation-triangle"></i> Fel & Retry
      </h2>

      {/* Stats Overview */}
      {stats && (
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-value">{stats.total_errors}</div>
            <div className="stat-label">Totalt fel</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.with_transcription}</div>
            <div className="stat-label">Med transkription</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.failed_audio_count}</div>
            <div className="stat-label">Ljudfiler</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {Object.keys(stats.by_error_code || {}).length}
            </div>
            <div className="stat-label">Feltyper</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'errors' ? 'active' : ''}`}
          onClick={() => setActiveTab('errors')}
        >
          <i className="fas fa-list"></i> Fel ({errors.length})
        </button>
        <button
          className={`tab ${activeTab === 'audio' ? 'active' : ''}`}
          onClick={() => setActiveTab('audio')}
        >
          <i className="fas fa-file-audio"></i> Ljudfiler ({failedAudio.length})
        </button>
        <button
          className={`tab ${activeTab === 'chunks' ? 'active' : ''}`}
          onClick={() => setActiveTab('chunks')}
        >
          <i className="fas fa-puzzle-piece"></i> Chunks ({chunks.total_count})
        </button>
        <button
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <i className="fas fa-chart-bar"></i> Statistik
        </button>
      </div>

      {/* Filters */}
      {activeTab === 'errors' && (
        <div className="filters">
          <select
            name="clinic_id"
            value={filters.clinic_id}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">Alla kliniker</option>
            {clinics.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select
            name="user_id"
            value={filters.user_id}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">Alla användare</option>
            {filteredUsers.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>

          <select
            name="error_code"
            value={filters.error_code}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">Alla felkoder</option>
            {errorCodes.map(code => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>

          <select
            name="has_transcription"
            value={filters.has_transcription}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">Alla</option>
            <option value="true">Med transkription</option>
            <option value="false">Utan transkription</option>
          </select>

          <input
            type="date"
            name="from_date"
            value={filters.from_date}
            onChange={handleFilterChange}
            className="filter-input"
            placeholder="Från datum"
          />

          <input
            type="date"
            name="to_date"
            value={filters.to_date}
            onChange={handleFilterChange}
            className="filter-input"
            placeholder="Till datum"
          />

          <button onClick={clearFilters} className="clear-filters-btn">
            <i className="fas fa-times"></i> Rensa
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="loading">Laddar...</div>
      ) : (
        <>
          {/* Errors Tab */}
          {activeTab === 'errors' && (
            <div className="errors-list">
              {errors.length === 0 ? (
                <div className="no-data">Inga fel hittades</div>
              ) : (
                <table className="errors-table">
                  <thead>
                    <tr>
                      <th>Tid</th>
                      <th>Felkod</th>
                      <th>Användare</th>
                      <th>Klinik</th>
                      <th>Transkription</th>
                      <th>Status</th>
                      <th>Åtgärder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map(error => (
                      <React.Fragment key={error.id}>
                        <tr
                          className={`error-row ${error.resolved ? 'resolved' : ''}`}
                          onClick={() => setExpandedError(
                            expandedError === error.id ? null : error.id
                          )}
                        >
                          <td>{formatDate(error.timestamp)}</td>
                          <td>
                            <span className={`error-code ${getErrorCodeClass(error.error_code)}`}>
                              {error.error_code}
                            </span>
                          </td>
                          <td>{error.user_name || '-'}</td>
                          <td>{error.clinic_name || '-'}</td>
                          <td>
                            {error.transcription_available ? (
                              <span className="badge badge-success">
                                {error.transcription_length} tecken
                              </span>
                            ) : (
                              <span className="badge badge-warning">Nej</span>
                            )}
                          </td>
                          <td>
                            {error.resolved ? (
                              <span className="badge badge-resolved">Löst</span>
                            ) : (
                              <span className="badge badge-open">Öppen</span>
                            )}
                          </td>
                          <td className="actions-cell" onClick={e => e.stopPropagation()}>
                            {error.transcription_available && error.session_id && !error.resolved && (
                              <button
                                className="action-btn retry-btn"
                                onClick={() => handleRetryJournal(error.session_id)}
                                disabled={retryLoading === `journal-${error.session_id}`}
                                title="Generera journal igen"
                              >
                                {retryLoading === `journal-${error.session_id}` ? (
                                  <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                  <i className="fas fa-redo"></i>
                                )}
                              </button>
                            )}
                            {!error.resolved && (
                              <button
                                className="action-btn resolve-btn"
                                onClick={() => handleResolveError(error.id)}
                                title="Markera som löst"
                              >
                                <i className="fas fa-check"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                        {expandedError === error.id && (
                          <tr className="expanded-row">
                            <td colSpan="7">
                              <div className="error-details">
                                <div className="detail-section">
                                  <h4>Beskrivning</h4>
                                  <p>{error.error_description}</p>
                                </div>
                                <div className="detail-section">
                                  <h4>Felmeddelande</h4>
                                  <pre>{error.error_message || '-'}</pre>
                                </div>
                                {error.stack_trace && (
                                  <div className="detail-section">
                                    <h4>Stack Trace</h4>
                                    <pre className="stack-trace">{error.stack_trace}</pre>
                                  </div>
                                )}
                                <div className="detail-section">
                                  <h4>Detaljer</h4>
                                  <ul>
                                    <li><strong>Error ID:</strong> {error.id}</li>
                                    <li><strong>Session ID:</strong> {error.session_id || '-'}</li>
                                    <li><strong>Template:</strong> {error.template_name || '-'}</li>
                                    <li><strong>User ID:</strong> {error.user_id || '-'}</li>
                                  </ul>
                                </div>
                                {error.resolved && (
                                  <div className="detail-section resolved-info">
                                    <h4>Löst</h4>
                                    <p>
                                      <strong>Tid:</strong> {formatDate(error.resolved_at)}<br />
                                      <strong>Anteckning:</strong> {error.resolution_note || '-'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Audio Tab */}
          {activeTab === 'audio' && (
            <div className="audio-list">
              {failedAudio.length === 0 ? (
                <div className="no-data">Inga misslyckade ljudfiler</div>
              ) : (
                <table className="audio-table">
                  <thead>
                    <tr>
                      <th>Fil</th>
                      <th>Storlek</th>
                      <th>Användare</th>
                      <th>Klinik</th>
                      <th>Fel</th>
                      <th>Tid</th>
                      <th>Åtgärder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedAudio.map(file => (
                      <tr key={file.filename}>
                        <td className="filename">{file.filename}</td>
                        <td>{file.size_mb} MB</td>
                        <td>{file.user_name || '-'}</td>
                        <td>{file.clinic_name || '-'}</td>
                        <td className="error-msg">{file.error_message || '-'}</td>
                        <td>{formatDate(file.failed_at || file.created_at)}</td>
                        <td className="actions-cell">
                          <button
                            className="action-btn retry-btn"
                            onClick={() => handleRetryTranscription(file.filename, false)}
                            disabled={retryLoading === `transcription-${file.filename}`}
                            title="Transkribera"
                          >
                            {retryLoading === `transcription-${file.filename}` ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-microphone"></i>
                            )}
                          </button>
                          <button
                            className="action-btn retry-all-btn"
                            onClick={() => handleRetryTranscription(file.filename, true)}
                            disabled={retryLoading === `transcription-${file.filename}`}
                            title="Transkribera + Journal"
                          >
                            <i className="fas fa-magic"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && stats && (
            <div className="stats-detail">
              <div className="stats-section">
                <h3>Fel per typ</h3>
                <div className="stats-grid">
                  {Object.entries(stats.by_error_code || {}).map(([code, count]) => (
                    <div key={code} className="stats-item">
                      <span className={`error-code ${getErrorCodeClass(code)}`}>{code}</span>
                      <span className="stats-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stats-section">
                <h3>Fel per klinik</h3>
                <div className="stats-grid">
                  {Object.entries(stats.by_clinic || {}).map(([clinic, count]) => (
                    <div key={clinic} className="stats-item">
                      <span className="clinic-name">{clinic}</span>
                      <span className="stats-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="stats-section">
                <h3>Fel senaste 30 dagarna</h3>
                <div className="daily-stats">
                  {Object.entries(stats.by_day || {})
                    .sort(([a], [b]) => b.localeCompare(a))
                    .slice(0, 14)
                    .map(([day, count]) => (
                      <div key={day} className="daily-item">
                        <span className="day">{day}</span>
                        <div className="bar" style={{ width: `${Math.min(count * 10, 100)}%` }}></div>
                        <span className="count">{count}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Chunks Tab */}
          {activeTab === 'chunks' && (
            <div className="chunks-container">
              {/* Search Section */}
              <div className="chunk-search-section">
                <h3><i className="fas fa-search"></i> Sök session</h3>
                <div className="search-row">
                  <input
                    type="number"
                    placeholder="Sessions-ID (t.ex. 12612)"
                    value={sessionIdSearch}
                    onChange={(e) => setSessionIdSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchSessionChunks()}
                    className="session-search-input"
                  />
                  <button
                    onClick={searchSessionChunks}
                    disabled={chunkLoading}
                    className="search-btn"
                  >
                    {chunkLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-search"></i>
                    )}
                    Sök
                  </button>
                </div>

                {/* Search Result */}
                {sessionChunks && (
                  <div className="session-search-result">
                    <div className="session-info-card">
                      <div className="session-header">
                        <h4>Session {sessionChunks.session.session_id}</h4>
                        <span className={`status-badge status-${sessionChunks.session.status}`}>
                          {sessionChunks.session.status}
                        </span>
                      </div>

                      <div className="session-details">
                        <div className="detail-row">
                          <span className="label">Användare:</span>
                          <span>{sessionChunks.session.user_name || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Skapad:</span>
                          <span>{formatDate(sessionChunks.session.created_at)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Transkription:</span>
                          <span>{sessionChunks.session.transcription_length} tecken</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Journal:</span>
                          <span>{sessionChunks.session.has_journal ? `${sessionChunks.session.journal_length} tecken` : 'Nej'}</span>
                        </div>
                      </div>

                      <div className="chunk-summary">
                        <div className="summary-stat">
                          <span className="stat-number">{sessionChunks.chunk_count}</span>
                          <span className="stat-label">Chunk-filer</span>
                        </div>
                        <div className="summary-stat">
                          <span className="stat-number">{sessionChunks.total_size_mb} MB</span>
                          <span className="stat-label">Total storlek</span>
                        </div>
                        {sessionChunks.match_method && (
                          <div className="summary-stat">
                            <span className="stat-number">
                              {sessionChunks.match_method === 'session_id' ? (
                                <i className="fas fa-check-circle" style={{color: '#28a745'}}></i>
                              ) : (
                                <i className="fas fa-clock" style={{color: '#ffc107'}}></i>
                              )}
                            </span>
                            <span className="stat-label">
                              {sessionChunks.match_method === 'session_id' ? 'Matchat på ID' : 'Matchat på tid'}
                            </span>
                          </div>
                        )}
                      </div>

                      {sessionChunks.chunk_count > 0 && (
                        <div className="session-actions">
                          <button
                            onClick={() => handleReprocessSession(sessionChunks.session.session_id)}
                            disabled={reprocessing}
                            className="action-btn reprocess-btn"
                          >
                            {reprocessing ? (
                              <><i className="fas fa-spinner fa-spin"></i> Bearbetar...</>
                            ) : (
                              <><i className="fas fa-redo"></i> Transkribera om</>
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteChunks(sessionChunks.session.session_id)}
                            className="action-btn delete-btn"
                          >
                            <i className="fas fa-trash"></i> Ta bort filer
                          </button>
                        </div>
                      )}

                      {sessionChunks.chunk_count === 0 && (
                        <div className="no-chunks-message">
                          <i className="fas fa-info-circle"></i>
                          Inga chunk-filer hittades för denna session
                        </div>
                      )}

                      {/* File list */}
                      {sessionChunks.chunks.length > 0 && (
                        <div className="chunk-file-list">
                          <h5>Filer ({sessionChunks.chunks.length})</h5>
                          <div className="file-list-scroll">
                            {sessionChunks.chunks.map((chunk, idx) => (
                              <div key={idx} className="chunk-file-item">
                                <span className="filename">{chunk.filename}</span>
                                <span className="filesize">{chunk.size_kb} KB</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Overview Section */}
              <div className="chunk-overview-section">
                <h3><i className="fas fa-database"></i> Alla chunk-filer ({chunks.total_count} st, {chunks.total_size_mb} MB)</h3>

                {/* Sessions with chunks */}
                {chunks.by_session && chunks.by_session.length > 0 && (
                  <div className="chunks-by-session">
                    <h4>Per session</h4>
                    <table className="chunks-table">
                      <thead>
                        <tr>
                          <th>Session</th>
                          <th>Användare</th>
                          <th>Status</th>
                          <th>Filer</th>
                          <th>Storlek</th>
                          <th>Transkription</th>
                          <th>Åtgärder</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chunks.by_session.map(session => (
                          <tr key={session.session_id}>
                            <td>
                              <button
                                className="session-link"
                                onClick={() => searchSessionChunks(session.session_id)}
                              >
                                {session.session_id}
                              </button>
                            </td>
                            <td>{session.user_name || '-'}</td>
                            <td>
                              <span className={`status-badge status-${session.session_status}`}>
                                {session.session_status}
                              </span>
                            </td>
                            <td>{session.file_count}</td>
                            <td>{(session.total_size_kb / 1024).toFixed(1)} MB</td>
                            <td>
                              {session.has_transcription ? (
                                <span className="badge badge-success">{session.transcription_length} tecken</span>
                              ) : (
                                <span className="badge badge-warning">Nej</span>
                              )}
                            </td>
                            <td>
                              <button
                                className="action-btn small reprocess-btn"
                                onClick={() => handleReprocessSession(session.session_id)}
                                disabled={reprocessing}
                                title="Transkribera om"
                              >
                                <i className="fas fa-redo"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Unknown chunks */}
                {chunks.unknown_by_date && chunks.unknown_by_date.length > 0 && (
                  <div className="unknown-chunks">
                    <h4>
                      <i className="fas fa-question-circle"></i> Utan session-ID ({chunks.unknown_count} st)
                    </h4>
                    <p className="help-text">
                      Dessa filer använder det gamla formatet utan session-ID.
                      De kan inte automatiskt matchas till en session.
                    </p>
                    <div className="unknown-by-date">
                      {chunks.unknown_by_date.map(dateGroup => (
                        <div key={dateGroup.date} className="date-group">
                          <span className="date">{dateGroup.date}</span>
                          <span className="count">{dateGroup.file_count} filer</span>
                          <span className="size">{(dateGroup.total_size_kb / 1024).toFixed(1)} MB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {chunks.total_count === 0 && (
                  <div className="no-data">
                    <i className="fas fa-check-circle"></i> Inga chunk-filer på servern
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminErrors;
